'use client';

import { useEffect, useState, useContext, type FC, useMemo } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Textarea,
  Input,
  Heading,
  Pill,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { TaxReturnReminderDialogProps } from './TaxReturnReminderDialog.types';
import { REMINDER_DEFAULT_MESSAGES } from '@/_constants/tax-return';
import {
  formatHtmlToPlainText,
  formatPlainTextToHtmlForBackend,
} from '@/_utils/sanitize';
import { sendReminder, updateReminderSettings } from '@/_api/admin-tax-returns';
import { UserContext } from '@/_contexts/UserProvider';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';

export const TaxReturnReminderDialog: FC<TaxReturnReminderDialogProps> = ({
  isOpen,
  onClose,
  selectedTaxReturns,
  reminderDisabledTaxReturnsCount,
  title,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { getTaxReturns } = useContext(AdminTaxReturnsContext);

  const [subject, setSubject] = useState<string>(
    REMINDER_DEFAULT_MESSAGES.email_subject,
  );
  const [emailMessage, setEmailMessage] = useState<string>(
    REMINDER_DEFAULT_MESSAGES.email_message,
  );
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const taxReturnMapped: Array<{ id: number; companyId: number }> =
    selectedTaxReturns
      .filter((tr) => tr.status !== 'finished')
      .map((tr) => ({ id: tr.id!, companyId: tr.company_id! }));

  useEffect(() => {
    if (isInitialized) return;

    if (selectedTaxReturns.length === 1) {
      const subject =
        selectedTaxReturns[0].customization?.reminder_email_subject;
      const message =
        selectedTaxReturns[0].customization?.reminder_email_message;
      if (subject) {
        setSubject(formatHtmlToPlainText(subject));
      }
      if (message) {
        setEmailMessage(formatHtmlToPlainText(message));
      }
      setIsInitialized(true);
    } else if (selectedTaxReturns.length > 1) {
      const hasSameSubject = selectedTaxReturns.every(
        (tr) =>
          tr.customization?.reminder_email_subject ===
          selectedTaxReturns[0].customization?.reminder_email_subject,
      );
      const hasSameMessage = selectedTaxReturns.every(
        (tr) =>
          tr.customization?.reminder_email_message ===
          selectedTaxReturns[0].customization?.reminder_email_message,
      );
      if (!hasSameSubject) {
        setSubject(REMINDER_DEFAULT_MESSAGES.email_subject);
        setIsDefault(true);
      }
      if (!hasSameMessage) {
        setEmailMessage(REMINDER_DEFAULT_MESSAGES.email_message);
        setIsDefault(true);
      }
      setIsInitialized(true);
    }
  }, [selectedTaxReturns, isInitialized]);

  const handleDialogClose = () => {
    setIsInitialized(false);
    onClose();
  };

  const hasChanges = useMemo(() => {
    const notDefault =
      subject !== REMINDER_DEFAULT_MESSAGES.email_subject ||
      emailMessage !== REMINDER_DEFAULT_MESSAGES.email_message;
    const notEmpty = !!subject && !!emailMessage;
    const notSame =
      selectedTaxReturns.length === 1
        ? subject !==
            selectedTaxReturns[0].customization?.reminder_email_subject ||
          emailMessage !==
            selectedTaxReturns[0].customization?.reminder_email_message
        : true;
    return notDefault && notEmpty && notSame;
  }, [subject, emailMessage, selectedTaxReturns]);

  const onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSubject(event.target.value);
  const onEmailMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => setEmailMessage(event.target.value);

  const onSendReminder = async () => {
    try {
      if (hasChanges) {
        const saveSuccess = await onSave(false);
        if (!saveSuccess) {
          return;
        }
      }

      const items = await Promise.all(
        taxReturnMapped.map(({ id, companyId }) =>
          sendReminder({
            id,
            companyId,
            token,
          }),
        ),
      );

      const hasError: boolean = items.some((item) => item.error);

      if (hasError) {
        addToast('error', 'Failed to send reminders');
        return;
      }

      addToast('success', 'Tax return reminders sent successfully');
    } catch (error) {
      console.error(error);
      addToast('error', 'Failed to send reminders');
    } finally {
      handleDialogClose();
      setIsDefault(false);
    }
  };

  const onSave = async (shouldClose: boolean = true) => {
    try {
      const response = await Promise.all(
        taxReturnMapped.map(({ id, companyId }) =>
          updateReminderSettings({
            id,
            companyId,
            reminderEmailSubject: subject,
            reminderEmailMessage: formatPlainTextToHtmlForBackend(emailMessage),
            token,
          }),
        ),
      );

      if (response.some((item) => item.error)) {
        addToast('error', 'Failed to update reminder settings');
        return false;
      }

      addToast('success', 'Reminder settings updated');
      await onReloadTaxReturns();
      return true;
    } catch (error) {
      addToast('error', 'Failed to update reminder settings');
      return false;
    } finally {
      if (shouldClose) {
        handleDialogClose();
      }
      setIsDefault(false);
    }
  };

  const onReloadTaxReturns = () => {
    getTaxReturns();
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleDialogClose} title={title}>
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          {reminderDisabledTaxReturnsCount &&
            !!reminderDisabledTaxReturnsCount && (
              <Alert type="info" icon="info">
                Reminders will not be sent to {reminderDisabledTaxReturnsCount}{' '}
                tax returns that are already completed (all signatures and
                payments are signed and paid) or marked as finished.
              </Alert>
            )}
          {isDefault && (
            <Alert type="info" icon="info">
              Reminder settings will default when multiple tax returns are
              selected and their subjects or messages do not match.{' '}
            </Alert>
          )}
          <>
            <Flex gap={2} isYCentered={true}>
              <Heading level={4}>Email</Heading>
              {isDefault && <Pill intent="info" label="Default" size="small" />}
            </Flex>
            <Input
              value={subject}
              label="Subject"
              onChange={onSubjectChange}
              isRequired={true}
            />
            <Textarea
              rows={10}
              value={emailMessage}
              label="Body"
              onChange={onEmailMessageChange}
              isRequired={true}
            />

            <ButtonGroup align="right" isSameLine>
              <Button
                intent="danger-secondary"
                onClick={handleDialogClose}
                value="Cancel"
              />
              <Button
                intent="secondary"
                onClick={() => onSave()}
                value="Save & Do Not Send"
                isDisabled={!hasChanges}
              />
              <Button
                intent="primary"
                onClick={onSendReminder}
                value="Send Now"
              />
            </ButtonGroup>
          </>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
